import englishMessages from 'ra-language-english';
import type { TranslationMessages } from 'ra-core';
import type { PayMessages } from './payMessages';

/**
 * Spain Spanish (es-ES), formal register — "Introduzca", not "Ingrese";
 * "Active", not "Habilite"; "correctamente", not "exitosamente"; always
 * "usted" constructions. This is the default, active locale.
 *
 * Built on the official react-admin English catalog (the framework's own
 * components need the full `ra.*` vocabulary — dozens of keys this
 * application does not render yet) with the strings this wave actually
 * shows overridden to formal Spanish, plus this application's own `pay.*`
 * namespace, entirely in Spanish. Later waves override more of `ra.*` as
 * they start rendering the react-admin components that use them (see the
 * B1 wave report, open items).
 */
const es: TranslationMessages & { pay: PayMessages } = {
  ...englishMessages,
  ra: {
    ...englishMessages.ra,
    action: {
      ...englishMessages.ra.action,
      close: 'Cerrar',
      refresh: 'Actualizar',
    },
    page: {
      ...englishMessages.ra.page,
      loading: 'Cargando…',
      not_found: 'Página no encontrada',
      error: 'Ha ocurrido un error',
    },
    message: {
      ...englishMessages.ra.message,
      error: 'Se ha producido un error y no se ha podido completar la solicitud.',
    },
    notification: {
      ...englishMessages.ra.notification,
      http_error: 'No se ha podido establecer comunicación con el servicio.',
      data_provider_error:
        'Error inesperado. Si el problema persiste, contacte con el equipo de soporte.',
    },
    boolean: {
      ...englishMessages.ra.boolean,
      true: 'Sí',
      false: 'No',
    },
  },
  pay: {
    appBar: {
      wordmarkBrand: 'Roommatik',
      wordmarkProduct: 'Pay',
      // v3.7 — el indicador de entorno no productivo del rail/barra superior
      // se mantiene deliberadamente en inglés en todos los idiomas
      // (hybrid-3c.md, igual que en las aplicaciones de referencia).
      environmentTest: 'TEST ENV',
      environmentLocal: 'LOCAL',
    },
    nav: {
      operations: 'Operaciones',
      statistics: 'Estadísticas',
      tenants: 'Tenants',
      applications: 'Aplicaciones',
      credentials: 'Credenciales',
      gatewayAccounts: 'Cuentas de pasarela',
      audit: 'Auditoría',
      testBench: 'Probar pago',
      groups: {
        operation: 'Operación',
        configuration: 'Configuración',
      },
    },
    profile: {
      roleLabelSuperAdmin: 'Administrador general de la plataforma',
      roleLabelAdmin: 'Administrador de la plataforma',
      roleLabelAuditor: 'Auditor de la plataforma',
      logout: 'Cerrar sesión',
    },
    sectionHeader: {
      help: 'Ayuda',
      filters: 'Filtros',
      refresh: 'Actualizar',
      export: 'Exportar',
      create: 'Crear',
    },
    filtersPopover: {
      title: 'Filtros',
      apply: 'Aplicar',
      clear: 'Limpiar',
    },
    filterableSelect: {
      noOptions: 'Sin resultados',
      loading: 'Cargando…',
      filterPlaceholder: 'Filtrar…',
    },
    emptyState: {
      comingSoonTitle: 'Esta sección estará disponible próximamente',
      comingSoonBody: 'Estamos preparando esta funcionalidad. Vuelva a consultarla más adelante.',
    },
    loadingState: {
      label: 'Cargando información…',
    },
    errorState: {
      title: 'No se ha podido completar la solicitud',
      body: 'Compruebe su conexión e inténtelo de nuevo. Si el problema persiste, contacte con soporte.',
      retry: 'Reintentar',
    },
    dataGrid: {
      showMore: 'Mostrar más detalles',
      showLess: 'Mostrar menos detalles',
      displayedRows: '%{from}–%{to} de %{count}',
    },
    accessDenied: {
      title: 'No tiene permisos para realizar esta acción',
      body: 'Su rol solo permite consultar la información de esta sección.',
    },
    noSession: {
      title: 'No hay ninguna sesión activa',
      body: 'Acceda desde el portal correspondiente para continuar. Si el problema persiste, póngase en contacto con soporte.',
    },
    notEntitled: {
      title: 'No tiene acceso a esta aplicación',
      body: 'Su cuenta ha iniciado sesión correctamente, pero no dispone de acceso a esta aplicación. Póngase en contacto con soporte para solicitarlo.',
    },
    accessUnavailable: {
      title: 'No se ha podido completar la comprobación de acceso',
      body: 'Vuelva a intentarlo en unos instantes. Si el problema persiste, póngase en contacto con soporte técnico.',
    },
    operations: {
      pageTitle: 'Operaciones',
      pageSubtitle: 'Operaciones de pago y agregados por establecimiento.',
      tabs: { list: 'Operaciones', aggregates: 'Agregados' },
      list: {
        emptyTitle: 'No se han encontrado operaciones',
        emptyBody:
          'Ajuste los filtros aplicados o amplíe el intervalo de fechas e inténtelo de nuevo.',
        errorTitle: 'No se ha podido cargar el listado de operaciones',
        columnTenant: 'Establecimiento',
        columnApplication: 'Aplicación',
        columnGateway: 'Pasarela',
        columnType: 'Tipo',
        columnStatus: 'Estado',
        columnAmount: 'Importe',
        columnOrderReference: 'Referencia',
        columnCreatedAt: 'Fecha de creación',
        columnUpdatedAt: 'Última actualización',
        viewDetail: 'Ver el detalle de la operación',
        pageLabel: 'Página',
        pageOf: 'de',
        rowsPerPage: 'Operaciones por página',
        exportStarted: 'Se ha iniciado la descarga del archivo CSV.',
        exportError: 'No se ha podido generar el archivo de exportación.',
      },
      detail: {
        back: 'Volver al listado de operaciones',
        notFoundTitle: 'No se ha encontrado la operación',
        notFoundBody: 'Compruebe el identificador o vuelva al listado de operaciones.',
        accessDeniedTitle: 'No tiene autorización para consultar esta operación',
        fieldTenant: 'Establecimiento',
        fieldApplication: 'Aplicación propietaria',
        fieldType: 'Tipo',
        fieldStatus: 'Estado',
        fieldAmount: 'Importe',
        fieldOrderReference: 'Referencia',
        fieldDescription: 'Descripción',
        fieldCustomerRef: 'Referencia de cliente',
        fieldGateway: 'Pasarela',
        fieldCreatedAt: 'Fecha de creación',
        fieldExpiresAt: 'Fecha de expiración',
        timelineTitle: 'Cronología de la operación',
        timelineEmpty: 'Todavía no se ha registrado ningún evento para esta operación.',
        operationAmount: 'Importe de la operación',
        operationReference: 'Referencia en la pasarela',
        gatewayCheckTitle: 'Estado en la pasarela',
        gatewayCheckNeverChecked: 'Todavía no se ha consultado el estado de este cobro con la pasarela.',
        gatewayCheckUnverifiable: 'No se ha podido verificar',
        gatewayCheckCheckedAt: 'Última comprobación',
        gatewayReconcileAction: 'Reconciliar con la pasarela',
        gatewayReconcileSuccess: 'El estado del cobro se ha actualizado correctamente.',
        gatewayReconcileUnsupported: 'La pasarela de este cobro no admite la comprobación automática de su estado.',
      },
      aggregates: {
        emptyTitle: 'No hay datos agregados para los filtros aplicados',
        emptyBody: 'Ajuste los filtros o el intervalo de fechas e inténtelo de nuevo.',
        fieldTotalCount: 'Operaciones creadas',
        fieldSuccessCount: 'Operaciones satisfactorias',
        fieldSuccessRate: 'Tasa de éxito',
        fieldAbandonmentRate: 'Tasa de abandono',
        fieldTotalAmount: 'Importe total',
        fieldRefundedAmount: 'Importe reembolsado',
        groupByLabel: 'Agrupar por',
        periodLabel: 'Periodo',
        bucketUnlabelled: 'Sin agrupación',
      },
      filters: {
        dateFrom: 'Fecha desde',
        dateTo: 'Fecha hasta',
        tenantCode: 'Establecimientos',
        tenantCodeHelp: 'Introduzca un código de establecimiento y pulse Intro para añadirlo.',
        tenantTag: 'Etiquetas de establecimiento',
        tenantTagHelp: 'Formato clave:valor, por ejemplo chain:acme. Pulse Intro para añadirla.',
        applicationCode: 'Aplicaciones',
        gateway: 'Pasarela',
        status: 'Estado',
        type: 'Tipo',
        amountMin: 'Importe mínimo',
        amountMax: 'Importe máximo',
        amountHelp: 'En unidades menores de la divisa (por ejemplo, céntimos).',
        orderReference: 'Referencia',
        sort: 'Ordenar por',
      },
      sort: {
        createdAtDesc: 'Fecha de creación (más recientes primero)',
        createdAtAsc: 'Fecha de creación (más antiguas primero)',
        amountDesc: 'Importe (mayor a menor)',
        amountAsc: 'Importe (menor a mayor)',
      },
      status: {
        created: 'Creada',
        session_consumed: 'Sesión consumida',
        redirected: 'Redirigida',
        authorized: 'Autorizada',
        captured: 'Capturada',
        failed: 'Fallida',
        cancelled: 'Cancelada',
        expired: 'Expirada',
        refunded: 'Reembolsada',
        partially_refunded: 'Reembolso parcial',
      },
      type: {
        sale: 'Venta',
        preauth: 'Preautorización',
        verify: 'Verificación',
        terminal: 'Terminal',
      },
      operationKind: {
        authorize: 'Autorización',
        capture: 'Captura',
        void: 'Anulación',
        refund: 'Reembolso',
        increment: 'Incremento',
      },
      operationStatus: {
        pending: 'Pendiente',
        succeeded: 'Correcta',
        failed: 'Fallida',
      },
      gatewayStatus: {
        awaiting_guest: 'El huésped no ha completado el proceso de pago',
        attempt_declined: 'El intento de pago fue rechazado',
        action_required: 'Pendiente de una verificación adicional',
        paid: 'El pago se completó en la pasarela',
      },
      timelineEvent: {
        created: 'Operación creada',
        session_consumed: 'Sesión de pago consumida',
        redirected: 'Redirección al huésped',
        webhook_received: 'Notificación recibida de la pasarela',
        status_changed: 'Cambio de estado',
        notification_sent: 'Notificación enviada a la aplicación',
        notification_retry: 'Reintento de notificación',
        notification_delivered: 'Notificación entregada correctamente',
      },
      period: {
        day: 'Día',
        week: 'Semana',
        month: 'Mes',
      },
      groupBy: {
        application: 'Aplicación',
        tenant: 'Establecimiento',
        gateway: 'Pasarela',
        period: 'Periodo',
        tag: 'Etiqueta',
      },
    },
    statistics: {
      pageTitle: 'Estadísticas',
      pageSubtitle: 'Volumen anual de cobros con tarjeta y desglose por establecimiento y etiqueta.',
      annual: {
        title: 'Cobros con tarjeta en %{year}',
        ofLimit: 'de %{limit} cobros anuales',
        percentUsed: '%{percent} del límite anual utilizado',
        remaining: '%{remaining} cobros restantes',
        warning: 'Se ha superado el 80 % del límite anual de cobros.',
        overLimit: 'Se ha superado el límite anual de cobros.',
        errorTitle: 'No se ha podido cargar el contador anual',
      },
      tabs: {
        tenant: 'Establecimiento',
        tag: 'Etiqueta',
        all: 'Todos',
      },
      views: {
        simple: 'Vista simple',
        extended: 'Vista extendida',
      },
      breakdown: {
        untagged: 'Sin etiqueta',
        totalsTitle: 'Totales',
        emptyTitle: 'No hay operaciones en el periodo seleccionado',
        emptyBody: 'Ajuste el intervalo de fechas e inténtelo de nuevo.',
        errorTitle: 'No se ha podido cargar el desglose',
      },
      table: {
        columnBucket: 'Grupo',
        columnCurrency: 'Moneda',
        columnCharges: 'Cobros',
        columnStarted: 'Operaciones iniciadas',
        totalsLabel: 'Totales',
        totalsOverallLabel: 'Total general',
      },
      exportStarted: 'Se ha iniciado la descarga del archivo CSV.',
      exportError: 'No se ha podido generar el archivo de exportación.',
    },
    tenants: {
      pageTitle: 'Establecimientos',
      pageSubtitle: 'Establecimientos configurados en la plataforma de pagos.',
      list: {
        emptyTitle: 'No se han encontrado establecimientos',
        emptyBody: 'Ajuste los filtros aplicados o cree un nuevo establecimiento.',
        errorTitle: 'No se ha podido cargar el listado de establecimientos',
        columnCode: 'Código',
        columnName: 'Nombre',
        columnStatus: 'Estado',
        columnCreatedAt: 'Fecha de alta',
        viewDetail: 'Ver el detalle del establecimiento',
        rowsPerPage: 'Establecimientos por página',
        createLabel: 'Crear establecimiento',
      },
      filters: {
        search: 'Buscar',
        searchHelp: 'Busca por código o nombre del establecimiento.',
        status: 'Estado',
      },
      status: {
        active: 'Activo',
        suspended: 'Suspendido',
      },
      create: {
        title: 'Crear establecimiento',
        fieldCode: 'Código',
        fieldCodeHelp:
          'Identificador permanente del establecimiento. No podrá modificarse posteriormente.',
        fieldName: 'Nombre',
        submit: 'Crear',
        cancel: 'Cancelar',
        success: 'El establecimiento se ha creado correctamente.',
        conflictError:
          'Ya existe un establecimiento con este código. Introduzca un código distinto.',
      },
      detail: {
        back: 'Volver al listado de establecimientos',
        notFoundTitle: 'No se ha encontrado el establecimiento',
        notFoundBody: 'Compruebe el código o vuelva al listado de establecimientos.',
        tabsGeneral: 'Datos generales',
        tabsPolicy: 'Política',
        fieldCode: 'Código',
        fieldName: 'Nombre',
        fieldStatus: 'Estado',
        fieldCreatedAt: 'Fecha de alta',
        editNameAction: 'Editar nombre',
        changeStatusAction: 'Cambiar estado',
      },
      editName: {
        title: 'Editar nombre del establecimiento',
        fieldName: 'Nombre',
        submit: 'Guardar',
        cancel: 'Cancelar',
        success: 'El nombre se ha actualizado correctamente.',
      },
      statusDialog: {
        title: 'Cambiar el estado del establecimiento',
        body: 'Un establecimiento suspendido conserva sus datos; únicamente se impide la creación de nuevas operaciones de pago.',
        fieldStatus: 'Nuevo estado',
        submit: 'Confirmar',
        cancel: 'Cancelar',
        success: 'El estado se ha actualizado correctamente.',
      },
      tags: {
        title: 'Reemplazar etiquetas',
        replaceNotice:
          'Esta operación sustituye el conjunto completo de etiquetas del establecimiento. Introduzca todas las etiquetas que desea conservar, no únicamente las nuevas.',
        empty: 'Este establecimiento no tiene etiquetas.',
        editAction: 'Reemplazar etiquetas',
        fieldKey: 'Clave',
        fieldValue: 'Valor',
        keyHelp: 'Solo minúsculas, números, guiones y guiones bajos.',
        add: 'Añadir etiqueta',
        remove: 'Eliminar',
        submit: 'Guardar',
        cancel: 'Cancelar',
        success: 'Las etiquetas se han actualizado correctamente.',
      },
      policy: {
        emptyTitle: 'Este establecimiento no tiene una política configurada',
        emptyBody:
          'Active una política para establecer límites de importe, divisas y tipos de operación permitidos.',
        createAction: 'Crear política',
        editAction: 'Editar política',
        errorTitle: 'No se ha podido cargar la política del establecimiento',
        fieldMaxAmount: 'Importe máximo',
        fieldMaxAmountHelp: 'En unidades menores de la divisa (por ejemplo, céntimos).',
        fieldCurrencies: 'Divisas permitidas',
        fieldCurrenciesHelp:
          'Código ISO 4217, por ejemplo EUR. Pulse Intro para añadir cada divisa.',
        fieldAllowedTypes: 'Tipos de operación permitidos',
        fieldAllowedReturnOrigins: 'Orígenes de retorno permitidos',
        fieldAllowedReturnOriginsHelp:
          'URLs de origen permitidas para returnUrl y cancelUrl. Pulse Intro para añadir cada una.',
        fieldConflictPolicy: 'Política ante referencia de pedido duplicada',
        conflictPolicy: {
          reject: 'Rechazar la nueva operación',
          return_existing: 'Devolver la operación existente',
        },
        submit: 'Guardar',
        cancel: 'Cancelar',
        success: 'La política se ha guardado correctamente.',
      },
    },
    applications: {
      pageTitle: 'Aplicaciones',
      pageSubtitle: 'Aplicaciones registradas para autenticarse frente a la API de pagos.',
      list: {
        emptyTitle: 'No se han encontrado aplicaciones',
        emptyBody: 'Cree una aplicación llamante para permitirle inyectar pagos.',
        errorTitle: 'No se ha podido cargar el listado de aplicaciones',
        columnCode: 'Código',
        columnName: 'Nombre',
        columnStatus: 'Estado',
        columnNotifyUrl: 'URL de notificación',
        columnCreatedAt: 'Fecha de alta',
        viewDetail: 'Ver el detalle de la aplicación',
        rowsPerPage: 'Aplicaciones por página',
        createLabel: 'Crear aplicación',
        notifyUrlEmpty: 'No configurada',
      },
      status: { active: 'Activa', suspended: 'Suspendida' },
      create: {
        title: 'Crear aplicación',
        fieldCode: 'Código',
        fieldCodeHelp:
          'Identificador público permanente de la aplicación. No podrá modificarse posteriormente.',
        fieldName: 'Nombre',
        fieldNotifyUrl: 'URL de notificación',
        fieldNotifySecret: 'Secreto de firma de notificaciones',
        fieldNotifySecretHelp:
          'Solo escritura. Obligatorio para firmar las notificaciones salientes si se establece una URL de notificación.',
        submit: 'Crear',
        cancel: 'Cancelar',
        success: 'La aplicación se ha creado correctamente.',
        conflictError: 'Ya existe una aplicación con este código. Introduzca un código distinto.',
      },
      detail: {
        back: 'Volver al listado de aplicaciones',
        notFoundTitle: 'No se ha encontrado la aplicación',
        notFoundBody: 'Compruebe el código o vuelva al listado de aplicaciones.',
        fieldCode: 'Código',
        fieldName: 'Nombre',
        fieldStatus: 'Estado',
        fieldNotifyUrl: 'URL de notificación',
        notifyUrlEmpty: 'No configurada',
        fieldCreatedAt: 'Fecha de alta',
        editAction: 'Editar aplicación',
      },
      edit: {
        title: 'Editar aplicación',
        fieldName: 'Nombre',
        fieldStatus: 'Estado',
        fieldNotifyUrl: 'URL de notificación',
        fieldNotifySecret: 'Secreto de firma de notificaciones',
        fieldNotifySecretHelp:
          'Solo escritura. Déjelo en blanco para conservar el secreto actual sin cambios.',
        submit: 'Guardar',
        cancel: 'Cancelar',
        success: 'La aplicación se ha actualizado correctamente.',
      },
      egressIps: {
        title: 'Direcciones IP de salida',
        intro:
          'La creación de cobros está restringida por un control de acceso del borde: solo las direcciones autorizadas pueden llamar.',
        errorTitle: 'No se han podido cargar las direcciones IP de salida',
        declaredTitle: 'Direcciones IP de salida declaradas',
        declaredEmpty: 'No hay direcciones declaradas.',
        editAction: 'Editar direcciones declaradas',
        chipListed: 'En la lista de autorizadas',
        chipMissing: 'Ausente de la lista',
        chipUnverified: 'No verificado',
        verificationUnavailable: 'No se ha podido comprobar el control de acceso del borde.',
        notApplicableNote:
          'Este entorno no está sujeto al control de acceso del borde: la lista de direcciones autorizadas se aplica únicamente en producción.',
        unmatchedTitle: 'Direcciones autorizadas que ninguna aplicación declara',
        columnIp: 'Dirección IP',
        columnCredential: 'Credencial',
        columnCalls: 'Llamadas',
        columnLastSeen: 'Última llamada',
        observedCaption: 'Direcciones observadas en los últimos %{days} días.',
        observedEmptyTitle: 'Sin llamadas observadas',
        observedEmptyBody:
          'Ninguna credencial de esta aplicación ha llamado en los últimos %{days} días.',
        dialog: {
          title: 'Editar direcciones IP de salida declaradas',
          fieldIps: 'Direcciones IP de salida declaradas',
          fieldIpsHelp:
            'Introduzca una dirección IPv4 o IPv6 y pulse Intro para añadirla. Sustituye el conjunto completo de direcciones declaradas.',
          submit: 'Guardar',
          cancel: 'Cancelar',
          success: 'Las direcciones IP de salida declaradas se han actualizado correctamente.',
        },
      },
    },
    credentials: {
      pageTitle: 'Credenciales de API',
      pageSubtitle: 'Claves de API emitidas para las aplicaciones registradas.',
      list: {
        emptyTitle: 'No se han encontrado credenciales de API',
        emptyBody:
          'Emita una credencial de API para que una aplicación autentique sus solicitudes servidor a servidor.',
        errorTitle: 'No se ha podido cargar el listado de credenciales',
        columnApplication: 'Aplicación',
        columnKeyId: 'Identificador de clave',
        columnStatus: 'Estado',
        columnScope: 'Alcance de establecimientos',
        columnRotatedAt: 'Última rotación',
        columnCreatedAt: 'Fecha de alta',
        viewDetail: 'Ver el detalle de la credencial',
        rowsPerPage: 'Credenciales por página',
        createLabel: 'Emitir credencial',
        neverRotated: 'Nunca rotada',
      },
      filters: {
        applicationCode: 'Aplicaciones',
        applicationCodeHelp: 'Introduzca un código de aplicación y pulse Intro para añadirlo.',
      },
      status: { active: 'Activa', revoked: 'Revocada' },
      scope: {
        allTenants: 'Todos los establecimientos',
        specificTenants: 'Establecimientos específicos',
      },
      create: {
        title: 'Emitir credencial de API',
        fieldApplication: 'Aplicación',
        fieldApplicationHelp: 'La aplicación llamante a la que pertenece esta credencial.',
        fieldAllowAllTenants: 'Permitir todos los establecimientos',
        fieldAllowedTenantCodes: 'Establecimientos autorizados',
        fieldAllowedTenantCodesHelp:
          'Introduzca un código de establecimiento y pulse Intro para añadirlo.',
        fieldIpAllowlist: 'Lista de IP permitidas',
        fieldIpAllowlistHelp:
          'Introduzca una IP o un rango CIDR y pulse Intro para añadirlo. Déjelo vacío para aceptar cualquier IP de origen.',
        submit: 'Emitir',
        cancel: 'Cancelar',
      },
      secretPanel: {
        createdTitle: 'Credencial emitida',
        rotatedTitle: 'Credencial rotada',
        warning:
          'Copie estos valores ahora. Se muestran exactamente una vez y no podrán recuperarse de nuevo por ninguna operación, ni siquiera por un administrador. Si los pierde, emita una nueva credencial o rote esta.',
        fieldSecret: 'Secreto',
        fieldSigningSecret: 'Secreto de firma',
        copy: 'Copiar',
        copied: 'Copiado al portapapeles.',
        close: 'He copiado los valores, cerrar',
        closeConfirmTitle: '¿Cerrar sin confirmar la copia?',
        closeConfirmBody: 'Una vez cierre este panel, estos valores no volverán a mostrarse.',
        closeConfirmConfirm: 'Cerrar de todos modos',
        closeConfirmCancel: 'Volver',
      },
      detail: {
        back: 'Volver al listado de credenciales',
        notFoundTitle: 'No se ha encontrado la credencial',
        notFoundBody: 'Compruebe el identificador o vuelva al listado de credenciales.',
        fieldId: 'Identificador',
        fieldApplication: 'Aplicación',
        fieldKeyId: 'Identificador de clave',
        fieldStatus: 'Estado',
        fieldScope: 'Alcance de establecimientos',
        fieldIpAllowlist: 'Lista de IP permitidas',
        ipAllowlistEmpty: 'Sin restricción: se acepta cualquier IP de origen.',
        fieldRotatedAt: 'Última rotación',
        fieldCreatedAt: 'Fecha de alta',
        neverRotated: 'Nunca rotada',
        rotateAction: 'Rotar',
        revokeAction: 'Revocar',
        editScopeAction: 'Editar alcance de establecimientos',
        editIpAllowlistAction: 'Editar lista de IP permitidas',
        revokedNotice: 'Esta credencial ha sido revocada. Ya no puede autenticar solicitudes.',
      },
      rotateDialog: {
        title: 'Rotar esta credencial',
        body: 'Se generará un nuevo secreto y un nuevo secreto de firma. El material actual dejará de funcionar de inmediato y no podrá recuperarse posteriormente.',
        submit: 'Rotar',
        cancel: 'Cancelar',
      },
      revokeDialog: {
        title: 'Revocar esta credencial',
        body: 'Una credencial revocada ya no puede autenticar solicitudes. Esta acción no se puede deshacer.',
        fieldReason: 'Motivo (opcional)',
        submit: 'Revocar',
        cancel: 'Cancelar',
        success: 'La credencial se ha revocado correctamente.',
      },
      scopeDialog: {
        title: 'Editar alcance de establecimientos',
        fieldAllowAllTenants: 'Permitir todos los establecimientos',
        fieldAllowedTenantCodes: 'Establecimientos autorizados',
        fieldAllowedTenantCodesHelp:
          'Introduzca un código de establecimiento y pulse Intro para añadirlo. Sustituye el conjunto completo.',
        submit: 'Guardar',
        cancel: 'Cancelar',
        success: 'El alcance de establecimientos se ha actualizado correctamente.',
      },
      ipAllowlistDialog: {
        title: 'Editar lista de IP permitidas',
        fieldIpAllowlist: 'Lista de IP permitidas',
        fieldIpAllowlistHelp:
          'Introduzca una IP o un rango CIDR y pulse Intro para añadirlo. Sustituye el conjunto completo; déjelo vacío para aceptar cualquier IP de origen.',
        submit: 'Guardar',
        cancel: 'Cancelar',
        success: 'La lista de IP permitidas se ha actualizado correctamente.',
      },
    },
    gatewayAccounts: {
      pageTitle: 'Cuentas de pasarela',
      pageSubtitle: 'Cuentas de pasarela de pago configuradas para los establecimientos.',
      list: {
        emptyTitle: 'No se han encontrado cuentas de pasarela',
        emptyBody:
          'Cree una cuenta de pasarela para un establecimiento para empezar a aceptar pagos a través de ella.',
        errorTitle: 'No se ha podido cargar el listado de cuentas de pasarela',
        columnTenant: 'Establecimiento',
        columnGateway: 'Pasarela',
        columnAlias: 'Alias',
        columnMode: 'Modo',
        columnStatus: 'Estado',
        columnCreatedAt: 'Fecha de alta',
        viewDetail: 'Ver el detalle de la cuenta de pasarela',
        rowsPerPage: 'Cuentas por página',
        createLabel: 'Crear cuenta de pasarela',
      },
      filters: {
        tenantCode: 'Establecimientos',
        tenantCodeHelp: 'Introduzca un código de establecimiento y pulse Intro para añadirlo.',
        gateway: 'Pasarela',
      },
      mode: { test: 'Prueba', live: 'Producción' },
      status: { active: 'Activa', suspended: 'Suspendida' },
      gateway: {
        redsys: 'Redsys',
        stripe: 'Stripe',
        adyen: 'Adyen',
        payten: 'Payten',
        plexo: 'Plexo',
      },
      create: {
        title: 'Crear cuenta de pasarela',
        sourceLabel: 'Origen de las credenciales',
        sourceReal: 'Credenciales reales',
        sourcePreset: 'Proveedor de pruebas (entorno de pruebas)',
        fieldTenant: 'Establecimiento',
        fieldTenantHelp: 'El establecimiento al que pertenece esta cuenta.',
        fieldGateway: 'Pasarela',
        fieldMode: 'Modo',
        fieldAlias: 'Alias',
        fieldCapabilities: 'Capacidades',
        fieldCapabilitiesHelp: 'Introduzca una capacidad y pulse Intro para añadirla.',
        fieldExternalAccountCode: 'Código de cuenta externo',
        fieldExternalTerminalId: 'Identificador de terminal externo',
        fieldExternalEndpointPrefix: 'Prefijo de conexión del comercio',
        fieldExternalEndpointPrefixHelp: 'Sólo para las pasarelas que asignan a cada comercio su propia dirección de conexión en modo real. Déjelo vacío si la pasarela no se lo ha facilitado.',
        optionalSuffix: '(opcional)',
        submit: 'Crear',
        cancel: 'Cancelar',
        success: 'La cuenta de pasarela se ha creado correctamente.',
      },
      detail: {
        back: 'Volver al listado de cuentas de pasarela',
        notFoundTitle: 'No se ha encontrado la cuenta de pasarela',
        notFoundBody: 'Compruebe el identificador o vuelva al listado de cuentas de pasarela.',
        tabsGeneral: 'Datos generales',
        tabsCredentials: 'Credenciales',
        fieldId: 'Identificador',
        fieldTenant: 'Establecimiento',
        fieldGateway: 'Pasarela',
        fieldAlias: 'Alias',
        fieldMode: 'Modo',
        fieldStatus: 'Estado',
        fieldCapabilities: 'Capacidades',
        capabilitiesEmpty: 'No hay capacidades configuradas.',
        fieldExternalAccountCode: 'Código de cuenta externo',
        fieldExternalTerminalId: 'Identificador de terminal externo',
        fieldExternalEndpointPrefix: 'Prefijo de conexión del comercio',
        notSet: 'No establecido',
        fieldCreatedAt: 'Fecha de alta',
        fieldPresetCode: 'Preajuste de pruebas',
        editAction: 'Editar cuenta',
        deleteAction: 'Eliminar cuenta',
        deleteTitle: '¿Eliminar esta cuenta?',
        deleteBody:
          'Se eliminarán la cuenta «%{alias}», sus credenciales y su clave. Sólo es posible mientras la cuenta no haya intervenido en ningún cobro; la operación queda registrada en la auditoría y no se puede deshacer.',
        deleteCancel: 'Cancelar',
        deleteConfirm: 'Eliminar',
        deleteSuccess: 'La cuenta se ha eliminado correctamente.',
        deleteInUse:
          'Esta cuenta ya ha intervenido en cobros y no se puede eliminar. Suspéndala para dejar de utilizarla.',
      },
      edit: {
        title: 'Editar cuenta de pasarela',
        fieldAlias: 'Alias',
        fieldCapabilities: 'Capacidades',
        fieldCapabilitiesHelp: 'Introduzca una capacidad y pulse Intro para añadirla.',
        fieldStatus: 'Estado',
        fieldExternalAccountCode: 'Código de cuenta externo',
        fieldExternalTerminalId: 'Identificador de terminal externo',
        fieldExternalEndpointPrefix: 'Prefijo de conexión del comercio',
        fieldExternalEndpointPrefixHelp: 'Sólo para las pasarelas que asignan a cada comercio su propia dirección de conexión en modo real. Déjelo vacío si la pasarela no se lo ha facilitado.',
        submit: 'Guardar',
        cancel: 'Cancelar',
        success: 'La cuenta de pasarela se ha actualizado correctamente.',
      },
      secrets: {
        emptyTitle: 'Todavía no se ha escrito ningún campo de credencial',
        emptyBody:
          'Escriba los campos de credencial que esta pasarela necesita para empezar a procesar pagos.',
        errorTitle: 'No se han podido cargar los campos de credencial',
        columnField: 'Campo',
        columnIsSet: 'Configurado',
        columnLast4: 'Últimos 4 caracteres',
        columnFingerprint: 'Huella',
        columnRotatedAt: 'Última rotación',
        isSetYes: 'Sí',
        isSetNo: 'No',
        notAvailable: 'No disponible',
        writeAction: 'Escribir credenciales',
        columnActions: 'Acciones',
        unusedChip: 'Sin uso',
        unusedTooltip: 'Esta pasarela no lee este campo. Se escribió por error y puede eliminarse.',
        removeAction: 'Eliminar campo',
        removeTitle: '¿Eliminar este campo?',
        removeBody:
          'Se destruirán todas las versiones guardadas de «%{fieldName}». Esta pasarela no lee este campo, así que los cobros no se ven afectados. La operación queda registrada en la auditoría y no se puede deshacer.',
        removeConfirm: 'Eliminar',
        removeCancel: 'Cancelar',
        removeSuccess: 'El campo se ha eliminado correctamente.',
      },
      credentialFields: {
        redsys: {
          signing_key: {
            label: 'Clave secreta de encriptación',
            help: 'Introduzca la clave tal y como se la entrega su entidad. Se guarda descodificada.',
          },
        },
        stripe: {
          secret_key: { label: 'Clave secreta de la API' },
          webhook_signing_secret: { label: 'Secreto de firma de las notificaciones' },
        },
        adyen: {
          api_key: { label: 'Clave de la API' },
          hmac_key: {
            label: 'Clave HMAC de las notificaciones',
            help: 'Cadena hexadecimal, tal y como la publica el panel del proveedor.',
          },
        },
        payten: {
          merchant_user: { label: 'Usuario de comercio' },
          merchant_password: { label: 'Contraseña de comercio' },
          signing_key: {
            label: 'Secreto de integridad de las notificaciones',
            help: 'Valor distinto de la contraseña de comercio.',
          },
        },
        plexo: {
          api_key: { label: 'Clave de la API' },
          webhook_secret: { label: 'Secreto de las notificaciones' },
        },
      },
      accountFields: {
        redsys: {
          externalAccountCode: 'Número de comercio (FUC)',
          externalTerminalId: 'Terminal',
        },
        adyen: {
          externalAccountCode: 'Cuenta de comercio',
          externalEndpointPrefix: 'Prefijo del punto de acceso de producción',
        },
        payten: {
          externalAccountCode: 'Identificador de comercio',
        },
      },
      secretsWriteDialog: {
        title: 'Escribir credenciales de la pasarela',
        body: 'Los valores se cifran de inmediato y no vuelven a mostrarse, ni siquiera a un administrador. Rellene únicamente los campos que desea establecer o rotar.',
        manualBody:
          'La plataforma no ha indicado qué credenciales necesita esta pasarela. Introduzca el nombre del campo y su formato manualmente.',
        fieldName: 'Nombre del campo',
        fieldNameHelp:
          'El nombre exacto del campo que espera esta pasarela (por ejemplo, merchant_code).',
        fieldValue: 'Valor',
        valueEncoding: 'Formato',
        valueEncodingHelp: 'Base64 solo si la pasarela publica así esta clave.',
        valueEncodingText: 'Texto',
        valueEncodingBase64: 'Base64',
        add: 'Añadir campo',
        remove: 'Eliminar',
        submit: 'Escribir',
        cancel: 'Cancelar',
        success: 'Los campos de credencial se han escrito correctamente.',
      },
    },
    audit: {
      pageTitle: 'Auditoría',
      pageSubtitle: 'Registro de auditoría de la actividad de la plataforma.',
      list: {
        emptyTitle: 'No se han encontrado registros de auditoría',
        emptyBody:
          'Ajuste los filtros aplicados o amplíe el intervalo de fechas e inténtelo de nuevo.',
        errorTitle: 'No se ha podido cargar el registro de auditoría',
        columnAt: 'Fecha',
        columnAction: 'Acción',
        columnActor: 'Actor',
        columnObject: 'Objeto',
        columnIp: 'Dirección IP',
        columnMetadata: 'Metadatos',
        columnTenant: 'Establecimiento',
        tenantPlatformWide: 'Acción de plataforma',
        rowsPerPage: 'Registros por página',
        viewDetail: 'Ver el detalle del registro',
        exportStarted: 'Se ha iniciado la descarga del archivo CSV.',
        exportError: 'No se ha podido generar el archivo de exportación.',
      },
      filters: {
        dateFrom: 'Fecha desde',
        dateTo: 'Fecha hasta',
        actor: 'Actor',
        actorHelp: 'Identificador exacto del actor.',
        actorType: 'Tipo de actor',
        action: 'Acción',
        objectType: 'Tipo de objeto',
        objectTypeHelp: 'Nombre del tipo de objeto afectado, por ejemplo tenant.',
        objectId: 'Identificador de objeto',
        objectIdHelp: 'Identificador exacto del objeto afectado.',
        objectIdNeedsObjectType: 'Seleccione primero un tipo de objeto.',
        tenantCode: 'Establecimientos',
        tenantCodeHelp: 'Introduzca un código de establecimiento y pulse Intro para añadirlo.',
      },
      actorType: {
        platform_admin: 'Administrador de la plataforma',
        platform_auditor: 'Auditor de la plataforma',
        platform_staff: 'Personal de la plataforma',
        application: 'Aplicación',
        system_process: 'Proceso del sistema',
        gateway: 'Pasarela',
        guest: 'Huésped',
      },
      objectType: {
        api_credential: 'Credencial de API',
        gateway_account: 'Cuenta de pasarela',
        gateway_secret: 'Credencial de pasarela',
        tenant: 'Establecimiento',
        tenant_policy: 'Política de establecimiento',
        payment_intent: 'Intención de pago',
        payment_operation: 'Operación de pago',
        checkout_session: 'Sesión de pago',
        gateway_event: 'Notificación de pasarela',
        outbound_notification: 'Notificación saliente',
        report_query: 'Consulta de informe',
        audit_log: 'Registro de auditoría',
        identity: 'Identidad',
      },
      metadataKey: {
        actor_role: 'Rol del actor',
        added: 'Añadidos',
        allowed_tenants: 'Establecimientos autorizados',
        alias: 'Alias',
        amount: 'Importe',
        application_id: 'Identificador de aplicación',
        attempt: 'Intento',
        attempted_object_id: 'Identificador de objeto solicitado',
        attempted_object_type: 'Tipo de objeto solicitado',
        attempted_status: 'Estado solicitado',
        attempted_user: 'Usuario indicado',
        changed_fields: 'Campos modificados',
        component: 'Componente',
        consumed_ip: 'IP de consumo',
        current_status: 'Estado actual',
        currency: 'Divisa',
        external_event_id: 'Identificador de evento externo',
        field_name: 'Nombre del campo',
        filters_hash: 'Huella de los filtros',
        fingerprint: 'Huella',
        format: 'Formato',
        gateway: 'Pasarela',
        gateway_account_id: 'Identificador de cuenta de pasarela',
        intent_id: 'Identificador de intención de pago',
        ip: 'IP',
        ip_allowlist_set: 'Lista de IP configurada',
        key: 'Clave',
        key_id: 'Identificador de clave',
        key_id_prefix: 'Prefijo del identificador de clave',
        key_management_ref: 'Referencia de gestión de claves',
        kind: 'Tipo',
        mfa_used: 'Segundo factor utilizado',
        mode: 'Modo',
        name: 'Nombre',
        new_status: 'Nuevo estado',
        new_value: 'Nuevo valor',
        notification_id: 'Identificador de notificación',
        operation_id: 'Identificador de operación',
        order_reference: 'Referencia de pedido',
        payment_intent_id: 'Identificador de intención de pago',
        previous_key_id: 'Identificador de clave anterior',
        previous_status: 'Estado anterior',
        previous_value: 'Valor anterior',
        previous_version: 'Versión anterior',
        new_version: 'Nueva versión',
        preset_code: 'Código de proveedor de pruebas',
        process_id: 'Identificador de proceso',
        reason: 'Motivo',
        removed: 'Eliminados',
        required_role_or_scope: 'Rol o alcance requerido',
        result_count: 'Resultados obtenidos',
        role: 'Rol',
        row_count: 'Filas exportadas',
        signature_ok: 'Firma válida',
        size_bytes: 'Tamaño en bytes',
        status: 'Estado',
        status_code: 'Código de estado',
        tenant_code: 'Código de establecimiento',
        tenant_codes_requested: 'Establecimientos solicitados',
        tenant_id: 'Identificador de establecimiento',
        token_fingerprint: 'Huella del token',
        trigger: 'Origen del cambio',
        type: 'Tipo',
        unauthorized_tenant_codes: 'Establecimientos no autorizados',
        url_host: 'Dominio de notificación',
        user_id: 'Identificador de usuario',
        version: 'Versión',
      },
      action: {
        credential: {
          created: 'Credencial de API creada',
          rotated: 'Credencial de API rotada',
          revoked: 'Credencial de API revocada',
          allowed_tenants_changed: 'Alcance de establecimientos de la credencial modificado',
          ip_allowlist_changed: 'Lista de IP permitidas de la credencial modificada',
        },
        gateway_account: {
          created: 'Cuenta de pasarela creada',
          updated: 'Cuenta de pasarela actualizada',
          status_changed: 'Estado de la cuenta de pasarela modificado',
        },
        gateway_secret: {
          written: 'Campo de credencial de pasarela escrito',
          rotated: 'Campo de credencial de pasarela rotado',
          retired: 'Campo de credencial de pasarela retirado',
        },
        vault: {
          dek_unwrapped: 'Clave de cifrado desenvuelta',
          credential_decrypted: 'Credencial de pasarela descifrada',
        },
        tenant: {
          created: 'Establecimiento creado',
          status_changed: 'Estado del establecimiento modificado',
          tag_changed: 'Etiqueta del establecimiento modificada',
        },
        tenant_policy: {
          changed: 'Política del establecimiento modificada',
        },
        payment_intent: {
          created: 'Intención de pago creada',
          status_changed: 'Estado de la intención de pago modificado',
          transition_rejected: 'Transición de estado de la intención de pago rechazada',
        },
        payment_operation: {
          recorded: 'Operación de pago registrada',
        },
        checkout_session: {
          consumed: 'Sesión de pago consumida',
          access_denied: 'Acceso a la sesión de pago denegado',
        },
        gateway_event: {
          received: 'Notificación de pasarela recibida',
          signature_invalid: 'Firma de la notificación de pasarela inválida',
          duplicate_discarded: 'Notificación de pasarela duplicada descartada',
        },
        notification: {
          enqueued: 'Notificación saliente encolada',
          attempted: 'Intento de notificación saliente',
          exhausted: 'Notificación saliente agotada',
          resent_manually: 'Notificación saliente reenviada manualmente',
        },
        report: {
          queried: 'Informe consultado',
          exported: 'Informe exportado',
          query_rejected: 'Consulta de informe rechazada',
        },
        auth: {
          login_succeeded: 'Inicio de sesión correcto',
          login_failed: 'Inicio de sesión fallido',
          access_denied: 'Acceso denegado',
          credential_rejected: 'Credencial de API rechazada',
        },
        audit_log: {
          queried: 'Registro de auditoría consultado',
          exported: 'Registro de auditoría exportado',
        },
        audit: {
          recording_paused: 'Registro de auditoría pausado',
          recording_resumed: 'Registro de auditoría reanudado',
        },
        test_bench: {
          preset_linked: 'Proveedor de pruebas vinculado',
          payment_launched: 'Pago de prueba lanzado',
        },
      },
      detail: {
        title: 'Detalle del registro de auditoría',
        close: 'Cerrar',
        fieldId: 'Identificador',
        fieldAt: 'Fecha',
        fieldActor: 'Actor',
        fieldActorType: 'Tipo de actor',
        fieldAction: 'Acción',
        fieldObjectType: 'Tipo de objeto',
        fieldObjectId: 'Identificador de objeto',
        fieldTenant: 'Establecimiento',
        tenantPlatformWide: 'Acción de plataforma, sin establecimiento asociado',
        fieldIp: 'IP de origen',
        ipNotAvailable: 'No disponible',
        metadataTitle: 'Datos de la acción',
        metadataEmpty: 'Esta acción no tiene datos adicionales.',
        booleanYes: 'Sí',
        booleanNo: 'No',
      },
    },
    testBench: {
      pageTitle: 'Probar pago',
      pageSubtitle: 'Lance un pago de prueba firmado y siga su cronología en tiempo real.',
      unavailable: {
        title: 'Esta función no está disponible en este momento',
        body: 'Póngase en contacto con soporte si necesita realizar un pago de prueba.',
      },
      launch: {
        title: 'Lanzar pago de prueba',
        readOnlyNotice: 'Su rol solo permite consultar los pagos de prueba lanzados.',
        fieldTenant: 'Establecimiento',
        fieldGatewayAccount: 'Cuenta de pasarela',
        fieldGatewayAccountHelp:
          'Seleccione el establecimiento para ver sus cuentas de pasarela disponibles.',
        noAccountsForTenant: 'Este establecimiento no tiene cuentas de pasarela configuradas.',
        fieldAmount: 'Importe (céntimos)',
        fieldAmountHelp: 'Equivale a',
        fieldCurrency: 'Divisa',
        submit: 'Lanzar pago de prueba',
      },
      result: {
        title: 'Pago de prueba lanzado correctamente',
        fieldOrderReference: 'Referencia',
        openSessionAction: 'Abrir página de pago',
      },
      recent: {
        title: 'Pagos de prueba recientes',
        emptyTitle: 'Todavía no se ha lanzado ningún pago de prueba',
        emptyBody: 'Lance un pago de prueba para empezar a seguir su ciclo de vida completo.',
        errorTitle: 'No se ha podido cargar el listado de pagos de prueba',
        columnOrderReference: 'Referencia',
        columnTenant: 'Establecimiento',
        columnAmount: 'Importe',
        columnStatus: 'Estado',
        columnCreatedAt: 'Fecha de creación',
        viewTimelineAction: 'Ver el seguimiento de este pago',
      },
      timeline: {
        title: 'Seguimiento en directo',
        emptyBody: 'Todavía no se ha registrado ningún evento para este pago.',
        errorTitle: 'No se ha podido cargar el seguimiento del pago',
        refreshAction: 'Actualizar',
        pollingNotice: 'Actualizando automáticamente cada pocos segundos…',
      },
      timelineEvent: {
        created: 'Pago creado',
        session_consumed: 'Sesión de pago consumida',
        redirected: 'Cliente redirigido a la pasarela',
        webhook_received: 'Notificación de la pasarela recibida',
        status_changed: 'Estado del pago actualizado',
        notification_sent: 'Notificación enviada a la aplicación',
        notification_delivered: 'Notificación entregada correctamente',
        notification_failed: 'No se ha podido entregar la notificación',
        sink_verified: 'Notificación verificada por el receptor de pruebas',
      },
      preset: {
        linkAction: 'Vincular proveedor de pruebas',
        dialogTitle: 'Vincular proveedor de pruebas',
        fieldTenant: 'Establecimiento',
        fieldPreset: 'Proveedor de pruebas',
        noPresets: 'No hay proveedores de pruebas disponibles en este momento.',
        fieldAlias: 'Alias',
        fieldAliasHelp:
          'Déjelo en blanco para usar el alias propuesto por el proveedor de pruebas.',
        submit: 'Vincular',
        cancel: 'Cancelar',
        success: 'La cuenta de pruebas se ha vinculado correctamente.',
      },
      testCard: {
        title: 'Tarjeta de prueba',
        fieldPan: 'Número de tarjeta',
        fieldExpiry: 'Caducidad',
        fieldCvv: 'Código de seguridad',
        fieldChallengeCode: 'Código de verificación',
        copy: 'Copiar',
        copied: 'Valor copiado al portapapeles.',
      },
    },
  },
};

export default es;
